import { DeliveryPreference } from '@client/_blessed/store/entities/departments/model';
import ChassisBadge from '@client/components/ChassisBadge/component';
import BundleRowItem from './components/BundleRowItem';
import { BundleGeneratorMatchInfo, GeneratorBundle } from '../store';
import { determineBundlePrimaryAndMatch } from '../utils/determine-bundle-primary-and-match';

import './style.css';

interface BundleRowProps {
  bundle: GeneratorBundle & { legs: BundleGeneratorMatchInfo[] };
  onMergeClick: (mergeInfo: {
    bundleId: string;
    primary: BundleGeneratorMatchInfo;
    match: BundleGeneratorMatchInfo;
  }) => Promise<void>;
  onMergeAndPostClick: (mergeInfo: {
    bundleId: string;
    primary: BundleGeneratorMatchInfo;
    match: BundleGeneratorMatchInfo;
  }) => Promise<void>;
  onRejectClick: (mergeInfo: { bundleId: string }) => Promise<void>;
  isMergingBundles: boolean;
}

function BundleRow({ bundle, onMergeClick, onMergeAndPostClick, onRejectClick, isMergingBundles }: BundleRowProps) {
  let baseClassName = 'BundleRow';

  if (bundle.creationStatus === 'rejected' || bundle.creationStatus === 'invalidated') {
    baseClassName = `${baseClassName}--rejected ${baseClassName}`;
  } else if (bundle.creationStatus === 'created') {
    baseClassName = `${baseClassName}--created ${baseClassName}`;
  }

  const { primary, match } = determineBundlePrimaryAndMatch(bundle);

  const primaryConsigneeDepartment = primary.stops.find((stop) => stop.type === 'consignee')?.department;
  let primaryConsigneeDeliveryPreference: DeliveryPreference<any> | null = null;
  if (primaryConsigneeDepartment) {
    primaryConsigneeDeliveryPreference =
      primary.importExport === 'import'
        ? primaryConsigneeDepartment.importDeliveryPreference || null
        : primaryConsigneeDepartment.exportDeliveryPreference || null;
  }

  const matchConsigneeDepartment = match.stops.find((stop) => stop.type === 'consignee')?.department;
  let matchConsigneeDeliveryPreference: DeliveryPreference<any> | null = null;
  if (matchConsigneeDepartment) {
    matchConsigneeDeliveryPreference =
      match.importExport === 'import'
        ? matchConsigneeDepartment.importDeliveryPreference || null
        : matchConsigneeDepartment.exportDeliveryPreference || null;
  }

  const canMergeAndPost =
    primaryConsigneeDeliveryPreference?.openTwentyFourHours && matchConsigneeDeliveryPreference?.openTwentyFourHours;
  let mergeAndPostButtonClassName = `${baseClassName}__action-button`;
  if (!canMergeAndPost) {
    mergeAndPostButtonClassName = `${baseClassName}__action-button--disabled ${mergeAndPostButtonClassName}`;
  }

  return (
    <div className={baseClassName}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        {bundle.type === 'terminal-dual' && bundle.metadata && bundle.metadata.isCrossTerminalJoin && (
          <div style={{ width: '100%', display: 'flex' }}>
            <ChassisBadge name="Cross Terminal" color="#ff2222" className={`${baseClassName}__badge`} />
          </div>
        )}

        <div className={`${baseClassName}__action-container`}>
          <div
            onClick={() => onMergeClick({ bundleId: bundle.bundleId, primary, match })}
            className={`${
              isMergingBundles ? `${baseClassName}__action-button--loading` : ''
            }${baseClassName}__action-button`}
          >
            Merge
          </div>
          <div
            className={`${
              isMergingBundles ? `${baseClassName}__action-button--loading` : ''
            }${mergeAndPostButtonClassName}`}
            onClick={() => {
              if (canMergeAndPost) {
                onMergeAndPostClick({ bundleId: bundle.bundleId, primary, match });
              }
            }}
          >
            Merge & Post
          </div>
          <div
            className={`${bundle.creationStatus === 'rejected' ? `${baseClassName}__action-button--disabled ` : ''}${
              isMergingBundles ? `${baseClassName}__action-button--loading ` : ''
            }${baseClassName}__action-button--reject ${baseClassName}__action-button`}
            onClick={() => {
              onRejectClick({ bundleId: bundle.bundleId });
            }}
          >
            Reject
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        {bundle.legs.map((leg, index) => (
          <BundleRowItem
            key={leg.legNumber}
            info={leg}
            generatorBundle={bundle}
            isFirstLeg={index === 0}
            isLastLeg={index === bundle.legs.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default BundleRow;
